import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { LANGS, stripLang, withLang } from "@/lib/lang";

// Absolute origin used in canonical/hreflang/og tags. Override per environment
// with REACT_APP_SITE_URL; falls back to the live domain.
export const SITE_URL = (
  process.env.REACT_APP_SITE_URL || "https://al-abrad.com"
).replace(/\/$/, "");

const OG_LOCALE = { ar: "ar_IQ", en: "en_US" };

const upsertMeta = (key, content, attr = "name") => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const removeMeta = (key, attr = "name") => {
  const el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (el) el.remove();
};

const upsertLink = (rel, href, hreflang) => {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

/**
 * Per-page metadata: title, description, canonical, hreflang alternates and
 * Open Graph tags. Optionally injects a JSON-LD block, replaced on each change
 * so stale schema never lingers between routes.
 */
export const useSeo = ({ title, description, image, jsonLd, noIndex = false }) => {
  const { lang } = useLang();
  const { pathname } = useLocation();

  // Serialize up front so callers don't have to memoize the object to keep the
  // effect from re-running on every render.
  const jsonLdText = jsonLd ? JSON.stringify(jsonLd) : null;

  useEffect(() => {
    const bare = stripLang(pathname);
    const canonical = `${SITE_URL}${withLang(lang, bare)}`;
    const ogImage = `${SITE_URL}${image || "/og-image.jpg"}`;

    if (title) {
      document.title = title;
      upsertMeta("og:title", title, "property");
      upsertMeta("twitter:title", title);
    }

    if (description) {
      upsertMeta("description", description);
      upsertMeta("og:description", description, "property");
      upsertMeta("twitter:description", description);
    }

    upsertMeta("og:type", "website", "property");
    upsertMeta("og:url", canonical, "property");
    upsertMeta("og:image", ogImage, "property");
    upsertMeta("og:locale", OG_LOCALE[lang], "property");
    upsertMeta("og:site_name", "Al-Abrad", "property");
    upsertMeta("twitter:card", "summary_large_image");
    upsertMeta("twitter:image", ogImage);

    upsertLink("canonical", canonical);
    LANGS.forEach((l) => upsertLink("alternate", `${SITE_URL}${withLang(l, bare)}`, l));
    upsertLink("alternate", `${SITE_URL}${withLang("ar", bare)}`, "x-default");

    if (noIndex) upsertMeta("robots", "noindex, follow");
    else removeMeta("robots");

    let script;
    if (jsonLdText) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.dataset.page = "true";
      script.textContent = jsonLdText;
      document.head.appendChild(script);
    }
    return () => {
      if (script) script.remove();
    };
  }, [title, description, image, jsonLdText, noIndex, lang, pathname]);
};
